import { cloneDeep, concat, set } from 'lodash';
import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components';
import FormDrawer from '~/components/FormDrawer';
import { useGetApplication } from '~/controllers/wizard';
import { PersonAddIcon } from '~/img';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { Person } from '~/types/Person';
import { Shareholders } from '~/types/Shareholders';
import buildNamePath from '~/utils/buildNamePath';
import { hasSoftDeclineFields } from '~/utils/softDeclinesFields';
import { useContactAdd } from '~/utils/useContactAdd';

import { shareholdersKey } from '.';
import { MatchingContactsModal } from '../MatchingContactsModal';
import IndividualForm, { formName } from './IndividualForm';
import { isShareholderEditButtonDisabled } from './utils';

export type Individual = {
  data: Partial<Person>;
  shares: number;
};
export interface IndividualAddProps extends Partial<FormItemInjectedProps<Shareholders>> {
  setSourceOfEditing: Dispatch<SetStateAction<string>>;
}

const IndividualAdd: React.FC<IndividualAddProps> = (props) => {
  const { value, onChange, setSourceOfEditing } = props;
  const shareholdersFormState = { shareholders: value };

  const { t } = useTranslation('fillApplication');
  const applicationQuery = useGetApplication();
  const hasIndividualsValidationWarnings = hasSoftDeclineFields(applicationQuery.data, 'shareholders.individuals[]');
  const newShareholderIndex = value?.individuals?.length ?? 0;

  const {
    drawerOpen,
    setDrawerOpen,
    modalOpen,
    onCancelModal,
    onCloseDrawer,
    contactState,
    setSelectedContact,
    handleCreateNewContact,
    handleSelectContact,
    onFinish,
  } = useContactAdd({
    sourceOfEditingValue: 'shareholder',
    setSourceOfEditing,
    onUpdate: (contact: Individual) => {
      const deepCloneValue = cloneDeep(value);
      set(deepCloneValue!, ['individuals', `${newShareholderIndex}`], contact);
      onChange?.(deepCloneValue!);
    },
    getNewContact: (newValues: ApplicationPayload) =>
      newValues.shareholders?.individuals?.[newShareholderIndex] as Individual,
    getNewContactData: (newContact: Individual) => newContact.data,
    setContactIds: (newContact: Individual, contactId: string) => {
      set(newContact, ['data', 'matchContactId'], contactId);
      set(newContact, ['data', 'contactId'], contactId);
    },
    setNewContactUserRoles: (newContact: Individual, userRoles: Array<string>) => {
      set(newContact, ['data', 'userRoles'], concat(newContact.data.userRoles, userRoles));
    },
  });

  return (
    <>
      <Button
        id="addNewIndividualShareholderButton"
        extraLarge
        adaptive
        type="default"
        icon={<PersonAddIcon />}
        disabled={isShareholderEditButtonDisabled(applicationQuery.data, hasIndividualsValidationWarnings)}
        onClick={() => setDrawerOpen(true)}
      >
        {t('Add new Individual shareholder')}
      </Button>
      <FormDrawer
        id="individualAddDrawer"
        open={drawerOpen}
        onClose={onCloseDrawer}
        title={t('Add new Individual shareholder')}
        formName={formName}
      >
        <MatchingContactsModal
          open={modalOpen}
          title={t(
            'This contact matches existing contacts. If you wish to update any of the contacts below, please select the contact you want to update, or create a new contact',
          )}
          listData={contactState.matchingContactList}
          selectedContact={contactState.selectedContact}
          onCancel={onCancelModal}
          setSelectedContact={setSelectedContact}
          handleCreateNewContact={handleCreateNewContact}
          handleSelectContact={handleSelectContact}
        />
        <IndividualForm
          onFinish={onFinish}
          initialValues={shareholdersFormState}
          n={buildNamePath([shareholdersKey, 'individuals', newShareholderIndex])}
          shareholderType="INDIVIDUAL"
        />
      </FormDrawer>
    </>
  );
};

export default IndividualAdd;
